import type { VisualEntity, TimestampedEntity } from '@/types';
// Cross-import типа WishlistItem через @x/ паттерн
import type { WishlistItem } from '@/features/wishlist-item';

/**
 * Константные массивы для enum типов - SINGLE SOURCE OF TRUTH
 * Используются для вывода типов и для zod валидации в schemas.ts
 */

export const PRIVACY_TYPES = ['private', 'public', 'friends', 'selected'] as const;
export const BOOKING_VISIBILITY_TYPES = ['show_names', 'hide_names', 'hide_all'] as const;

/**
 * Роли соавторов вишлиста
 */
export const COLLABORATOR_ROLES = ['owner', 'editor'] as const;

/**
 * Статусы приглашений соавторов
 */
export const COLLABORATOR_STATUSES = ['pending', 'accepted', 'declined'] as const;

/**
 * Тип приватности вишлиста
 */
export type PrivacyType = typeof PRIVACY_TYPES[number];

/**
 * Тип видимости бронирования
 */
export type BookingVisibilityType = typeof BOOKING_VISIBILITY_TYPES[number];

/**
 * Роль соавтора вишлиста
 */
export type CollaboratorRole = typeof COLLABORATOR_ROLES[number];

/**
 * Статус приглашения соавтора
 */
export type CollaboratorStatus = typeof COLLABORATOR_STATUSES[number];

export interface Wishlist extends VisualEntity, TimestampedEntity {
  userId: string; // ID владельца вишлиста
  iconId?: string;
  description?: string;
  icon?: string; // Emoji иконка
  itemCount?: number;
  eventDate?: Date;
  privacy?: PrivacyType;
  bookingVisibility?: BookingVisibilityType;
  favoriteCount?: number;
  items?: WishlistItem[]; // Массив желаний в вишлисте (populated join)
  collaborators?: WishlistCollaborator[]; // Список соавторов
}

/**
 * Соавтор вишлиста
 */
export interface WishlistCollaborator {
  id: string;
  wishlistId: string;
  userId: string;
  role: CollaboratorRole;
  invitedBy: string;
  status: CollaboratorStatus;
  invitedAt: Date;
  acceptedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  // Populated данные (join)
  user?: {
    id: string;
    username: string;
    fullName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
  inviter?: {
    id: string;
    username: string;
    fullName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
}

/**
 * Данные для создания приглашения соавтора
 */
export interface InviteCollaboratorInput {
  wishlistId: string;
  userId: string;
  role: CollaboratorRole;
}

/**
 * Данные для ответа на приглашение
 */
export interface RespondToInvitationInput {
  collaboratorId: string;
  status: 'accepted' | 'declined';
}

/**
 * Данные для создания/обновления вишлиста
 */
export interface WishlistInput {
  title: string;
  description?: string;
  iconId: string;
  eventDate?: Date;
  privacy: PrivacyType;
  bookingVisibility: BookingVisibilityType;
  selectedCollaboratorIds?: string[]; // ID друзей для приглашения (только при создании)
}

/**
 * Данные для обновления вишлиста (только изменяемые поля)
 */
export type WishlistUpdateData = Partial<Omit<WishlistInput, 'selectedCollaboratorIds'>>;

/**
 * Публичный вишлист с данными владельца (для каталога)
 */
export interface PublicWishlistWithUser extends Wishlist {
  /** Добавлен ли вишлист в избранное текущим пользователем */
  isFavorite?: boolean;
  user?: {
    id: string;
    displayName: string;
    lastName?: string;
    username: string;
    avatarUrl?: string;
  };
}

/**
 * Опция сортировки публичных вишлистов
 */
export type SortOption = 'popular' | 'newest' | 'favorites' | 'oldest';

/**
 * Приглашение в соавторы с данными вишлиста
 */
export interface CollaboratorInviteWithWishlist extends WishlistCollaborator {
  wishlist?: {
    id: string;
    title: string;
    icon?: string;
  };
}

/**
 * Статистика пользователя (количество вишлистов и желаний)
 */
export interface UserStats {
  wishlistCount: number;
  itemCount: number;
}